import { ICustomResponseCode } from "hooks/translations.interfaces";
import { useTranslations } from "hooks/useTranslations";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { searchQuerySelector } from "redux/searchQuery";
import {
  selectedItinerarySelector,
  setRevalidateResult,
} from "redux/selectedItinerary";
import { useFetchFlightsQuery } from "redux/services/flightSearch";
import { UseAppDispatch } from "redux/store";
import { resetAdditionalProducts } from "redux/additionalProducts";
import { resetShoppingCart } from "redux/shoppingCart";
import { resetCheckoutSteps, setShouldRevalidate } from "redux/checkoutSteps";
import {
  setTravelersList,
  travelerInformationSelector,
} from "redux/travelerInformation";
import { setupTravelers } from "components/Checkout/TravelerInformation/setupTravelers";
import { ErrorHandler } from "./ErrorHandler";
import { IRevalidateResponse } from "./responsePopup.interfaces";
import { ResponseIconMap } from "./ResponseIconMap";
import {
  CtaDialog,
  SlotMachineLoader,
  dateObjToDayDateString,
  dateTimeStringToHoursMinutes,
  deepCloneObject,
  formatPrice,
  useConfig,
} from "@thekilroygroup/shared-ui";
import { getCurrency } from "utils/numberUtils";
import { useNavigate, useLocation } from "react-router-dom";
import { buildQueryString } from "../../utils/queryStrings";

interface IProps {
  revalidateResult: any;
  setIsRevalidated: (boolean: boolean) => void;
  confirmAndNavigateTo: (path: string) => void;
}

export const RevalidateHandler = (props: IProps) => {
  const {
    revalidateResult,
    setIsRevalidated = () => {},
    confirmAndNavigateTo = () => {},
  } = props;
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { itineraries } = useSelector(selectedItinerarySelector);
  const { travelers: currentTravelers } = useSelector(
    travelerInformationSelector,
  );

  const { translations } = useTranslations();
  const { locale, appConfig } = useConfig();
  const dispatch = UseAppDispatch();
  const { isFetching, data, isError } = revalidateResult;
  const searchQuery = useSelector(searchQuerySelector);
  const { refetch } = useFetchFlightsQuery({
    searchForm: searchQuery,
    config: appConfig,
  });

  function cleanAndNavigateTo() {
    dispatch(resetCheckoutSteps());
    dispatch(resetAdditionalProducts());
    dispatch(resetShoppingCart());
    dispatch(
      setTravelersList(setupTravelers(data.itineraries[0], currentTravelers)),
    );
    dispatch(setShouldRevalidate(false));

    setIsRevalidated(false);
    confirmAndNavigateTo("/checkout");
  }

  // SCENARIO  NO CHANGES OR 2004
  useEffect(() => {
    if (!isFetching && !isError && (!data?.changes || responseCode2004(data))) {
      dispatch(setRevalidateResult(revalidateResult.data));
      if (pathname !== "/checkout") cleanAndNavigateTo();
    }
  }, [revalidateResult]);

  /**
   * TODO remove when 2004 is removed from the backend
   * @param response
   */
  const responseCode2004 = (response: IRevalidateResponse) => {
    if (response.changes?.some((e) => e.changeCode === "2004")) {
      return true;
    }
    return false;
  };

  const getResponse = (response: IRevalidateResponse) => {
    const customResponseCodes: ICustomResponseCode[] = response.changes.map(
      (code) => code.changeCode,
    );

    const responseCode = customResponseCodes[0];
    const responseTranslations = translations.customResponseCodes[responseCode];

    const from = response.changes[0].from;
    const to = response.changes[0].to;
    let replacedSubtitle = responseTranslations.subtitle;

    if (responseCode === "2001") {
      /**
       * Case 2001
       * The ticket price has changed
       */
      const fromPrice = formatPrice(
        from,
        locale,
        getCurrency(data.itineraries[0]),
      );
      const toPrice = formatPrice(to, locale, getCurrency(data.itineraries[0]));
      replacedSubtitle = replacedSubtitle
        .replaceAll("##fromPrice", fromPrice)
        .replaceAll("##toPrice", toPrice);
    }

    /**
     * Case 2002 || 2003
     * The departure date time has changed
     * * The arrival date time has changed
     */
    if (responseCode === "2002" || responseCode === "2003") {
      const fromTime = `${dateObjToDayDateString(
        new Date(from.substring(0, 19)),
        locale,
      )} ${dateTimeStringToHoursMinutes(from, locale)}`;

      const toTime = `${dateObjToDayDateString(
        new Date(to.substring(0, 19)),
        locale,
      )} ${dateTimeStringToHoursMinutes(to, locale)}`;

      replacedSubtitle = replacedSubtitle
        .replaceAll("##fromTime", fromTime)
        .replaceAll("##toTime", toTime);
    }

    if (
      responseCode === "2001" ||
      responseCode === "2002" ||
      responseCode === "2003"
    ) {
      return (
        <CtaDialog
          {...responseTranslations}
          subtitle={replacedSubtitle}
          icon={
            (responseTranslations.icon &&
              ResponseIconMap[responseTranslations.icon]) ||
            undefined
          }
          onClose={() => {
            refetch();
            setIsRevalidated(false);
          }}
          ctaAction={() => {
            setIsRevalidated(false);
            if (
              deepCloneObject(response.itineraries[0]) !==
              deepCloneObject(itineraries[0])
            ) {
              dispatch(setRevalidateResult(response));
            }
            cleanAndNavigateTo();
          }}
          cta2Action={() => {
            refetch();
            setIsRevalidated(false);
          }}
        />
      );
    }

    /**
     * Case 2005
     * Branded fare does not exist
     */
    if (responseCode === "2005") {
      const responseTranslations = translations.customResponseCodes[4006];
      return (
        <CtaDialog
          {...responseTranslations}
          icon={
            (responseTranslations.icon &&
              ResponseIconMap[responseTranslations.icon]) ||
            undefined
          }
          onClose={() => {
            setIsRevalidated(false);
            navigate(`/search-results?${buildQueryString(searchQuery)}`);
            refetch();
          }}
          ctaAction={() => {
            setIsRevalidated(false);
            navigate(`/search-results?${buildQueryString(searchQuery)}`);
            refetch();
          }}
        />
      );
    }
  };

  return (
    <>
      {isFetching && (
        <SlotMachineLoader
          title={translations.searchResultsPage.loader.isRevalidatingTitle}
        />
      )}
      {!isFetching && isError && (
        <>
          <ErrorHandler
            errorResult={revalidateResult}
            setShowErrorPopup={() => setIsRevalidated(false)}
            triggerRefetch={refetch}
          />
        </>
      )}
      {!isFetching &&
        data &&
        data?.changes?.length > 0 &&
        getResponse(revalidateResult.data)}
    </>
  );
};
